/**
 * Servicio para obtener credenciales LLM
 * Basado en AccessLLM.cs
 */

import { LLMCredentials } from '../types';
import configService from './configService';
import axios from 'axios';

interface LoginResponse {
  tokens: {
    access: string;
    refresh: string;
    to_connect?: string;
  };
  success?: boolean;
  message?: string;
}

interface CredentialsResponse {
  success: boolean;
  payload: Array<{
    openai_key: string;
    anthropic_key: string;
  }>;
}

export class LLMCredentialsService {
  private credentials: LLMCredentials | null = null;

  /**
   * Login y obtención de credenciales LLM
   */
  async loginAndGetCredentials(): Promise<LLMCredentials> {
    const config = configService.getConfig();
    const usePRO = !config.testingPhase;
    const useInspirex = true; // true for education, false for innovatex

    let adminUser: string;
    let adminPass: string;

    // Usar credenciales de staging solo si estamos en modo de desarrollo (testingPhase = true)
    // En producción (testingPhase = false), usar credenciales de producción
    if (config.testingWebSocket && config.testingPhase) {
      adminUser = 'llmstaging';
      adminPass = '-M4!35jA4D7*';
    } else {
      adminUser = useInspirex ? 'education' : 'innovatex';
      adminPass = useInspirex ? 'y]5m3eU@#7SC' : 'c982sME0n=#c';
    }

    const environment = usePRO ? 'PRO' : 'DEV';
    const customer = useInspirex ? config.client : 'innovatex';

    const loginUrl = `${config.baseUrlLLM}/api/v1/login/`;
    const credentialsUrl = `${config.baseUrlLLM}/api/v1/credentials/?customer=${customer}&environment=${environment}`;


    // Login
    const loginResponse = await this.performLogin(loginUrl, adminUser, adminPass, config.client);
    
    // Obtener credenciales
    const credentials = await this.fetchCredentials(credentialsUrl, loginResponse.tokens.access);

    this.credentials = {
      openaiKey: credentials.openaiKey,
      anthropicKey: credentials.anthropicKey,
      accessToken: loginResponse.tokens.access,
      refreshToken: loginResponse.tokens.refresh,
      toConnect: loginResponse.tokens.to_connect || '',
    };

    // Guardar en configService
    configService.setAccessToken(this.credentials.accessToken);
    configService.setRefreshToken(this.credentials.refreshToken);

    return this.credentials;
  }

  private async performLogin(
    url: string,
    username: string,
    password: string,
    toConnect: string
  ): Promise<LoginResponse> {
    try {
      const response = await axios.post<LoginResponse>(
        url,
        {
          username,
          password,
          to_connect: toConnect,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  private async fetchCredentials(
    url: string,
    token: string
  ): Promise<{ openaiKey: string; anthropicKey: string }> {
    try {
      const response = await axios.get<CredentialsResponse>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success && response.data.payload.length > 0) {
        const payload = response.data.payload[0];
        return {
          openaiKey: payload.openai_key,
          anthropicKey: payload.anthropic_key,
        };
      } else {
        throw new Error('Failed to retrieve credentials.');
      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        // Token expirado, intentar refrescar
        await this.refreshToken();
        // Reintentar
        return this.fetchCredentials(url, configService.getConfig().accessToken!);
      }
      throw error;
    }
  }

  private async refreshToken(): Promise<void> {
    const config = configService.getConfig();
    if (!config.refreshToken) {
      throw new Error('No refresh token available');
    }

    const refreshUrl = `${config.baseUrlLLM}/api/v1/auth/refresh/`;
    const response = await axios.post<{ access: string }>(
      refreshUrl,
      {
        refresh: config.refreshToken,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    configService.setAccessToken(response.data.access);
  }

  getCredentials(): LLMCredentials | null {
    return this.credentials;
  }
}

export const llmCredentialsService = new LLMCredentialsService();
export default llmCredentialsService;


